/**
 * Rate-limit policies API client.
 *
 * Mirrors `backend/src/hub/routes/rate_limits.py`. One row per
 * "thing being throttled" — either an ingress source (we receive) or
 * an egress target (we send). Two rows have ``is_default=true`` and
 * act as the global fallback for their type; they're editable but
 * never deletable.
 */
import { api } from "./api";


export type RateLimitType = "ingress" | "egress";
export type OnBreach = "queue" | "reject" | "shed";
export type EgressTargetKind = "http_host" | "connector";


export interface RateLimit {
  id: string;
  type: RateLimitType;
  description: string | null;
  /** True for the seed defaults — deletable=false, but fields editable. */
  is_default: boolean;
  ingress_source_id: string | null;
  egress_target_kind: string | null;
  egress_target_value: string | null;
  /** NULL on either limit dimension = uncapped on that axis. */
  max_concurrency: number | null;
  max_qps: number | null;
  burst: number | null;
  on_breach: OnBreach;
  enabled: boolean;
  tags: string[];
  created_by: string;
  created_at: string;
  updated_at: string | null;
}


export interface RateLimitCreate {
  type: RateLimitType;
  description?: string | null;
  ingress_source_id?: string | null;
  egress_target_kind?: EgressTargetKind | null;
  egress_target_value?: string | null;
  max_concurrency?: number | null;
  max_qps?: number | null;
  burst?: number | null;
  on_breach?: OnBreach;
  enabled?: boolean;
  tags?: string[];
}


/** Update — type + target identity are NOT updatable (delete+recreate). */
export interface RateLimitUpdate {
  description?: string | null;
  max_concurrency?: number | null;
  max_qps?: number | null;
  burst?: number | null;
  on_breach?: OnBreach;
  enabled?: boolean;
  tags?: string[];
}


export interface DiscoveredTarget {
  kind: EgressTargetKind;
  value: string;
  deliveries_count: number;
}


export const rateLimitsApi = {
  list: (params?: { type?: RateLimitType; enabled?: boolean }) =>
    api.get<RateLimit[]>("/rate-limits", { query: params }),

  get: (id: string) => api.get<RateLimit>(`/rate-limits/${id}`),

  create: (body: RateLimitCreate) => api.post<RateLimit>("/rate-limits", body),

  update: (id: string, body: RateLimitUpdate) =>
    api.patch<RateLimit>(`/rate-limits/${id}`, body),

  remove: (id: string) => api.delete<void>(`/rate-limits/${id}`),

  discoveredTargets: (params?: { kind?: EgressTargetKind; limit?: number }) =>
    api.get<DiscoveredTarget[]>("/rate-limits/discovered-targets", {
      query: params,
    }),
};
